import React from "react";
import { ElementFactory, Question, Serializer } from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";
import MapInput from "../nodes/Logic/Inputs/MapInput";

export const SURVEY_MAP_QUESTION_TYPE = "geo-json";
const SURVEY_MAP_QUESTION_ALIAS = "map";
const GOOGLE_MAPS_SCRIPT_ID = "google-maps-script";

let googleMapsLoadPromise: Promise<any> | null = null;

const ADDRESS_SEARCH_DEBOUNCE_MS = 500;
const ADDRESS_SEARCH_RESULT_LIMIT = 10;

export class QuestionMapModel extends Question {
  getType() {
    return SURVEY_MAP_QUESTION_TYPE;
  }
}

Serializer.addClass(
  SURVEY_MAP_QUESTION_TYPE,
  [
    { name: "liveUpdate:boolean", default: false },
    { name: "liveUpdateInterval:number", default: 60, minValue: 1 },
  ],
  () => new QuestionMapModel(""),
  "question"
);

ElementFactory.Instance.registerElement(SURVEY_MAP_QUESTION_TYPE, (name) => {
  return new QuestionMapModel(name);
});

ElementFactory.Instance.registerElement(SURVEY_MAP_QUESTION_ALIAS, (name) => {
  return new QuestionMapModel(name);
});

function createGeoJsonFeatureFromCoordinates(lat: number, lng: number) {
  return JSON.stringify({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [lng, lat],
    },
  });
}

function createGeoJsonFeature(position: GeolocationPosition) {
  return createGeoJsonFeatureFromCoordinates(
    position.coords.latitude,
    position.coords.longitude
  );
}

function getGoogleMapsApiKey() {
  return (window as any).GM_API_KEY || "";
}

function loadGoogleMaps(): Promise<any> {
  const googleWindow = window as any;

  if (googleWindow.google?.maps?.places?.PlacesService) {
    return Promise.resolve(googleWindow.google.maps);
  }

  if (googleWindow.google?.maps?.importLibrary) {
    return googleWindow.google.maps
      .importLibrary("places")
      .then((): any => googleWindow.google.maps);
  }

  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return Promise.reject(new Error("Google Maps API key is not configured."));
  }

  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    if (existingScript) {
      if (googleWindow.google?.maps && !googleWindow.google.maps.places) {
        reject(new Error("Google Places could not be loaded."));
        return;
      }

      existingScript.addEventListener("load", () =>
        resolve(googleWindow.google.maps)
      );
      existingScript.addEventListener("error", () =>
        reject(new Error("Google Maps could not be loaded."))
      );
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places&v=3.64`;
    script.onload = () => resolve(googleWindow.google.maps);
    script.onerror = () => reject(new Error("Google Maps could not be loaded."));

    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
}

function getGeoJsonErrorMessage(error?: GeolocationPositionError) {
  if (!error) {
    return "Could not get your location.";
  }

  switch (error.code) {
    case error.PERMISSION_DENIED:
      return "Location permission was denied.";
    case error.POSITION_UNAVAILABLE:
      return "Your location is currently unavailable.";
    case error.TIMEOUT:
      return "Getting your location timed out.";
    default:
      return "Could not get your location.";
  }
}

export class SurveyQuestionMap extends SurveyQuestionElementBase {
  private addressSearchTimeout: number | null = null;
  private addressSearchRequestId = 0;
  private liveLocationWatchId: number | null = null;

  constructor(props: any) {
    super(props);
    this.state = {
      isLoading: false,
      isGeocoding: false,
      error: null,
      hasLocation: Boolean(this.question.value),
      address: "",
      addressResults: [],
    };
  }

  get question() {
    return this.questionBase as QuestionMapModel;
  }

  get isReadOnly() {
    return (
      this.question.getPropertyValue("readOnly") || this.question.isDesignMode
    );
  }

  get isLiveUpdateEnabled() {
    return this.question.getPropertyValue("liveUpdate") === true;
  }

  startLiveLocationUpdates = () => {
    if (
      !this.isLiveUpdateEnabled ||
      this.question.isDesignMode ||
      this.liveLocationWatchId !== null ||
      !navigator.geolocation
    ) {
      return;
    }

    this.liveLocationWatchId = navigator.geolocation.watchPosition(
      (position) => {
        this.question.value = createGeoJsonFeature(position);
        this.setState({ hasLocation: true, error: null });
      },
      (error) => this.setState({ error: getGeoJsonErrorMessage(error) }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  stopLiveLocationUpdates = () => {
    if (this.liveLocationWatchId !== null) {
      navigator.geolocation.clearWatch(this.liveLocationWatchId);
      this.liveLocationWatchId = null;
    }
  };

  get value() {
    const value = this.question.value;

    if (Array.isArray(value)) {
      return value;
    }

    return value ? [value] : [];
  }

  handleMapChange = (values: string[]) => {
    this.question.value = values[0] || null;
    this.setState({
      hasLocation: Boolean(values[0]),
      error: null,
      addressResults: [],
    });
  };

  handleGetLocation = () => {
    if (!navigator.geolocation) {
      this.setState({ error: "Location is not supported by this browser." });
      return;
    }

    this.setState({ isLoading: true, error: null });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.question.value = createGeoJsonFeature(position);
        this.setState({ isLoading: false, error: null, hasLocation: true });
      },
      (error) => {
        this.setState({
          isLoading: false,
          error: getGeoJsonErrorMessage(error),
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  handleAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const address = event.target.value;
    this.setState({ address, addressResults: [], error: null });
    this.queueAddressSearch(address);
  };

  componentDidMount() {
    this.startLiveLocationUpdates();
  }

  componentWillUnmount() {
    this.clearAddressSearchTimeout();
    this.stopLiveLocationUpdates();
  }

  clearAddressSearchTimeout = () => {
    if (this.addressSearchTimeout !== null) {
      window.clearTimeout(this.addressSearchTimeout);
      this.addressSearchTimeout = null;
    }
  };

  queueAddressSearch = (address: string) => {
    this.clearAddressSearchTimeout();

    if (!address.trim()) {
      this.addressSearchRequestId += 1;
      this.setState({ isGeocoding: false, addressResults: [] });
      return;
    }

    this.addressSearchTimeout = window.setTimeout(() => {
      this.searchAddress(address, false);
    }, ADDRESS_SEARCH_DEBOUNCE_MS);
  };

  handleAddressResultSelect = (result: any) => {
    const location = result?.geometry?.location;

    if (!location) {
      this.setState({ error: "Address could not be found." });
      return;
    }

    this.question.value = createGeoJsonFeatureFromCoordinates(
      location.lat(),
      location.lng()
    );
    this.setState({
      error: null,
      hasLocation: true,
      address: result?.formatted_address || result?.name || this.state.address,
      addressResults: [],
    });
  };

  searchAddress = (address: string, showEmptyError: boolean) => {
    const trimmedAddress = address.trim();

    if (!trimmedAddress) {
      this.setState({
        error: showEmptyError ? "Enter an address to search." : null,
        addressResults: [],
      });
      return;
    }

    const requestId = this.addressSearchRequestId + 1;
    this.addressSearchRequestId = requestId;
    this.setState({ isGeocoding: true, error: null, addressResults: [] });

    loadGoogleMaps()
      .then((maps: any) => {
        const placesContainer = document.createElement("div");
        const placesService = new maps.places.PlacesService(placesContainer);

        placesService.textSearch(
          { query: trimmedAddress },
          (results: any[], status: string) => {
            if (requestId !== this.addressSearchRequestId) {
              return;
            }

            const addressResults = (results || []).filter(
              (result) => result?.geometry?.location
            ).slice(0, ADDRESS_SEARCH_RESULT_LIMIT);

            if (
              status !== maps.places.PlacesServiceStatus.OK ||
              !addressResults.length
            ) {
              this.setState({
                isGeocoding: false,
                error: showEmptyError ? "Address could not be found." : null,
                addressResults: [],
              });
              return;
            }

            this.setState({
              isGeocoding: false,
              error: null,
              addressResults,
            });
          }
        );
      })
      .catch((loadError: any) => {
        if (requestId !== this.addressSearchRequestId) {
          return;
        }

        this.setState({ isGeocoding: false, error: loadError.message });
      });
  };

  handleFindAddress = () => {
    const { address } = this.state as { address: string };
    this.clearAddressSearchTimeout();
    this.searchAddress(address, true);
  };

  renderElement() {
    const {
      isLoading,
      isGeocoding,
      error,
      hasLocation,
      address,
      addressResults,
    } = this.state as {
      isLoading: boolean;
      isGeocoding: boolean;
      error: string | null;
      hasLocation: boolean;
      address: string;
      addressResults: any[];
    };

    return (
      <div className="d-flex flex-column gap-2 w-100" style={{ minWidth: 0 }}>
        {this.isLiveUpdateEnabled && (
          <div className="text-muted small">Live location updates are active.</div>
        )}
        <div
          className="text-muted small"
          style={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowWrap: "anywhere" }}
        >
          Click the button below to get your location from your browser
          automatically.
        </div>
        <button
          type="button"
          className="sd-btn sd-btn--action"
          style={{ maxWidth: "100%", whiteSpace: "normal" }}
          disabled={this.isReadOnly || isLoading || isGeocoding}
          onClick={this.handleGetLocation}
        >
          Get your location from the browser
        </button>
        <div
          className="text-muted small"
          style={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowWrap: "anywhere" }}
        >
          OR type your address below.
        </div>
        <div className="d-flex flex-wrap gap-2 w-100" style={{ minWidth: 0 }}>
          <input
            type="text"
            className="form-control"
            style={{ flex: "1 1 220px", minWidth: 0 }}
            value={address}
            disabled={this.isReadOnly}
            onChange={this.handleAddressChange}
            placeholder="Address"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                this.handleFindAddress();
              }
            }}
          />
          <button
            type="button"
            className="sd-btn sd-btn--action"
            style={{ flex: "1 1 160px", whiteSpace: "normal" }}
            disabled={this.isReadOnly || isGeocoding}
            onClick={this.handleFindAddress}
          >
            {isGeocoding ? "Finding..." : "Find address"}
          </button>
        </div>
        {addressResults.length > 0 && (
          <div className="list-group shadow-sm">
            {addressResults.map((result, index) => (
              <button
                key={result.place_id || index}
                type="button"
                className="list-group-item list-group-item-action text-start"
                disabled={this.isReadOnly}
                onClick={() => this.handleAddressResultSelect(result)}
              >
                {result.formatted_address || result.name}
              </button>
            ))}
          </div>
        )}
        {hasLocation && !error && (
          <div className="text-success small">Location selected.</div>
        )}
        {error && <div className="text-danger small">{error}</div>}
        {hasLocation && (
          <MapInput
            mapDrawingModes={["marker"]}
            values={this.value}
            onChange={this.handleMapChange}
            isMultiple={false}
            isReadOnly={this.isReadOnly}
          />
        )}
      </div>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(
  SURVEY_MAP_QUESTION_TYPE,
  ((props: any) => React.createElement(SurveyQuestionMap, props)) as any
);

ReactQuestionFactory.Instance.registerQuestion(
  SURVEY_MAP_QUESTION_ALIAS,
  ((props: any) => React.createElement(SurveyQuestionMap, props)) as any
);
